/**
 * feAI – Frontend
 * src/pages/AgentiDashboardPage.tsx
 *
 * Dashboard lato Agenti – SD, SA, SC, CC
 * Mostra statistiche agente per agente, con dettaglio attivi/disattivi
 * e ripartizione dinamica per canale (Infility).
 *
 * Per Admin e Inserter il totale agenti attivi viene suddiviso dinamicamente
 * in tutti i canali attivi conteggiati dal backend (channel_breakdown).
 */

import React, { useState, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';

// ── Types ────────────────────────────────────────────────────────────────────

interface ChannelBreakdownItem {
  canale: string;
  sottocanale: string | null;
  label: string;
  agenti_attivi: number;
  agenti_disattivi: number;
  totale: number;
}

interface AgentStats {
  success: boolean;
  agenti_attivi: number;
  agenti_disattivi: number;
  contratti_positivi: number;
  contratti_negativi: number;
  totale_sottoposti: number;
  // Estesi per il dettaglio Infility (canale 2013/2017)
  agenti_attivi_infinity?: number;
  agenti_disattivi_infinity?: number;
  agenti_attivi_altri?: number;
  agenti_disattivi_altri?: number;
  // Breakdown completo e dinamico per canale
  channel_breakdown?: ChannelBreakdownItem[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function parseJwt(token: string): Record<string, unknown> | null {
  try {
    return JSON.parse(atob(token.split('.')[1]!));
  } catch {
    return null;
  }
}

const formatNumber = (val: number): string => {
  if (val == null) return '—';
  return val.toLocaleString('it-IT');
};

function isAdminFromPayload(payload: Record<string, unknown> | null): boolean {
  if (!payload) return false;
  const role = String(payload['role'] ?? '').toUpperCase().trim();
  const legacyRoleId = payload['legacy_role_id'];
  return role === 'ADM' || role === 'ADMIN' || legacyRoleId === 1;
}

function isInserterFromPayload(payload: Record<string, unknown> | null): boolean {
  if (!payload) return false;
  const legacyRoleId = payload['legacy_role_id'];
  const role = String(payload['role'] ?? '').toUpperCase().trim();
  // INSERTER has legacy_role_id = 10 (canale INTERNI)
  return legacyRoleId === 10 || role === 'INSERTER';
}

// ── Styles ───────────────────────────────────────────────────────────────────

const S = {
  wrapper: {
    padding: '32px 40px',
    overflowY: 'auto' as const,
    flex: 1,
  },
  header: {
    marginBottom: 28,
    display: 'flex',
    justifyContent: 'space-between' as const,
    alignItems: 'flex-start' as const,
    flexWrap: 'wrap' as const,
    gap: 16,
  },
  titleSection: {
    flex: 1,
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 800,
    color: '#0F1F18',
    letterSpacing: '-0.02em',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: '0.92rem',
    color: '#4A635A',
  },
  badgeSD: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 14px',
    borderRadius: 999,
    fontSize: '0.78rem',
    fontWeight: 700,
    background: 'rgba(0, 161, 87, 0.12)',
    color: '#008D4C',
    border: '1px solid rgba(0, 161, 87, 0.20)',
    marginTop: 8,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 16,
    marginBottom: 28,
  },
  statCard: (borderColor: string) => ({
    background: '#fff',
    borderRadius: 14,
    padding: '20px 20px',
    border: '1px solid #E2EAE5',
    borderLeft: `4px solid ${borderColor}`,
    boxShadow: '0 2px 12px rgba(0, 142, 77, 0.06)',
    transition: 'transform 0.15s, box-shadow 0.15s',
  } as React.CSSProperties),
  statIcon: {
    fontSize: '1.3rem',
    marginBottom: 8,
  },
  statLabel: {
    fontSize: '0.68rem',
    fontWeight: 700,
    color: '#8AA896',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    marginBottom: 4,
  },
  statValue: {
    fontSize: '1.7rem',
    fontWeight: 800,
    color: '#0F1F18',
    letterSpacing: '-0.03em',
  },
  statSub: {
    fontSize: '0.78rem',
    color: '#4A635A',
    marginTop: 4,
  },
  section: {
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #E2EAE5',
    boxShadow: '0 2px 12px rgba(0, 142, 77, 0.06)',
    padding: '24px 28px',
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0F1F18',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  userInfo: {
    background: 'linear-gradient(135deg, #F0F4F1, #E2EAE5)',
    borderRadius: 12,
    padding: '18px 22px',
    marginBottom: 24,
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #00A157, #64BE6F)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 800,
    fontSize: '1.1rem',
    flexShrink: 0,
  },
  userName: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0F1F18',
  },
  userRole: {
    fontSize: '0.82rem',
    color: '#4A635A',
    marginTop: 2,
  },
};

// ── Component ────────────────────────────────────────────────────────────────

export const AgentiDashboardPage: React.FC = () => {
  const token = localStorage.getItem('feai_access_token') ?? '';
  const payload = parseJwt(token);
  const rawUsername = (payload?.['username'] as string) ?? '—';
  const username = rawUsername.replace(/@aziendaonline$/i, '');
  const role = (payload?.['role'] as string) ?? '—';
  const initials = String(username).charAt(0).toUpperCase();

  // ── Stats ──
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Carica le statistiche della dashboard
  useEffect(() => {
    let cancelled = false;

    async function fetchStats() {
      try {
        setLoading(true);
        setError(null);
        const res = await apiClient.get<AgentStats>('/api/v1/core/report/dashboard/stats');
        if (!cancelled) {
          setStats(res.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, []);

  // ── Ruolo utente ──
  const isAdmin = isAdminFromPayload(payload);
  const isInserter = isInserterFromPayload(payload);

  // Determina il titolo in base al ruolo
  const dashboardTitle = isAdmin
    ? 'DashBoard Admin'
    : isInserter
      ? 'DashBoard Inserter'
      : 'DashBoard Agenti';

  // ── Derivati ──
  const attivi = stats?.agenti_attivi ?? 0;
  const disattivi = stats?.agenti_disattivi ?? 0;
  const totali = stats?.totale_sottoposti ?? 0;

  // ── Breakdown dinamico per canale ──
  // Usa il channel_breakdown dal backend (tutti i canali attivi) se disponibile;
  // altrimenti fallback sui campi singoli legacy.
  const channelBreakdown = stats?.channel_breakdown ?? [];

  // Infility = canale Infinity (2013 / sottocanale 2017)
  const infilityItem = channelBreakdown.find(c => c.label === 'Infility');
  const attiviInfility    = infilityItem?.agenti_attivi     ?? stats?.agenti_attivi_infinity ?? 0;
  const disattiviInfility = infilityItem?.agenti_disattivi  ?? stats?.agenti_disattivi_infinity ?? 0;

  // Interni = solo canale 'interni' (canale='interni', NON tutti i non-Infinity)
  const interniItem = channelBreakdown.find(c => c.label === 'Interni');
  const attiviInterni  = interniItem?.agenti_attivi     ?? stats?.agenti_attivi_altri ?? 0;
  const disattiviInterni = interniItem?.agenti_disattivi  ?? stats?.agenti_disattivi_altri ?? 0;

  // Per retrocompatibilità espongo anche le variabili vecchie usate nel resto del layout
  const attiviInfinity = attiviInfility;
  const disattiviInfinity = disattiviInfility;
  const attiviAltri = attiviInterni;
  const disattiviAltri = disattiviInterni;

  // Il dettaglio per canale (Infility / Interni) è visibile solo per Admin e Inserter
  const showChannelDetail = isAdmin || isInserter;

  return (
    <div style={S.wrapper}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.titleSection}>
          <h1 style={S.title}>{dashboardTitle}</h1>
          <p style={S.subtitle}>
            {isAdmin
              ? 'Panoramica completa di tutti gli agenti del sistema — attivi, disattivi e contratti'
              : isInserter
                ? 'Panoramica completa di tutti gli agenti del sistema — attivi, disattivi e contratti'
                : 'Panoramica completa degli agenti sotto la tua gerarchia — attivi, disattivi e ripartizione per canale (Infility)'
            }
          </p>
          <div style={S.badgeSD}>
            {isAdmin ? '👤 Admin' : isInserter ? '👤 Inserter' : `🎯 SD ${String(payload?.['cod_agente'] ?? '—')}`} · {totali} agenti totali
          </div>
        </div>
      </div>

      {/* User info */}
      <div style={S.userInfo}>
        <div style={S.avatar}>{initials}</div>
        <div>
          <div style={S.userName}>{String(username)}</div>
          <div style={S.userRole}>Ruolo: {String(role)} · Cod. Agente: {String(payload?.['cod_agente'] ?? '—')}</div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <span style={{ ...S.badgeSD, background: 'rgba(0, 161, 87, 0.10)', color: '#00A157', border: '1px solid rgba(0, 161, 87, 0.20)' }}>
            ● Online
          </span>
        </div>
      </div>

      {/* Status cards */}
      <div style={S.statsGrid}>
        {/* Attivi */}
        <div style={S.statCard('#00A157')}>
          <div style={S.statIcon}>✅</div>
          <div style={S.statLabel}>Agenti Attivi</div>
          <div style={S.statValue}>
            {loading ? '…' : error ? '!' : formatNumber(attivi)}
          </div>
          {!loading && !error && showChannelDetail && (
            <div style={{ ...S.statSub, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ color: attiviInfinity > 0 ? '#00A157' : '#8AA896' }}>
                ♾️ Infility: {formatNumber(attiviInfinity)}
              </span>
              {attiviAltri > 0 && (
                <span style={{ color: '#8AA896' }}>
                  🏢 Interni: {formatNumber(attiviAltri)}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Disattivi */}
        <div style={S.statCard('#DC2626')}>
          <div style={S.statIcon}>⛔</div>
          <div style={S.statLabel}>Agenti Disattivi</div>
          <div style={{ ...S.statValue, color: disattivi > 0 ? '#DC2626' : '#6B7280' }}>
            {loading ? '…' : error ? '!' : formatNumber(disattivi)}
          </div>
          {!loading && !error && showChannelDetail && (
            <div style={{ ...S.statSub, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ color: disattiviInfinity > 0 ? '#DC2626' : '#8AA896' }}>
                ♾️ Infility: {formatNumber(disattiviInfinity)}
              </span>
              {disattiviAltri > 0 && (
                <span style={{ color: '#8AA896' }}>
                  🏢 Interni: {formatNumber(disattiviAltri)}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Totali */}
        <div style={S.statCard('#2563EB')}>
          <div style={S.statIcon}>👥</div>
          <div style={S.statLabel}>Totale Sottoposti</div>
          <div style={S.statValue}>
            {loading ? '…' : error ? '—' : formatNumber(totali)}
          </div>
          {!loading && !error && showChannelDetail ? (
            <div style={{ ...S.statSub, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span>♾️ Infility: {formatNumber(attiviInfinity + disattiviInfinity)}</span>
              {(attiviAltri + disattiviAltri) > 0 && (
                <span style={{ color: '#8AA896' }}>
                  🏢 Interni: {formatNumber(attiviAltri + disattiviAltri)}
                </span>
              )}
            </div>
          ) : (
            <div style={S.statSub}>
              di cui {formatNumber(attivi)} attivi · {formatNumber(disattivi)} disattivi
            </div>
          )}
        </div>

        {/* Contratti Positivi */}
        <div style={S.statCard('#008D4C')}>
          <div style={S.statIcon}>📈</div>
          <div style={S.statLabel}>Contratti Positivi</div>
          <div style={S.statValue}>
            {loading ? '…' : error ? '—' : formatNumber(stats?.contratti_positivi ?? 0)}
          </div>
        </div>

        {/* Contratti Negativi */}
        <div style={S.statCard('#F59E0B')}>
          <div style={S.statIcon}>📉</div>
          <div style={S.statLabel}>Contratti Negativi</div>
          <div style={S.statValue}>
            {loading ? '…' : error ? '—' : formatNumber(stats?.contratti_negativi ?? 0)}
          </div>
        </div>
      </div>

      {/* Error / Warning per disattivi */}
      {!loading && !error && disattivi === 0 && totali > 0 && (
        <div style={{
          ...S.section,
          borderLeft: '4px solid #F59E0B',
          background: 'rgba(245, 158, 11, 0.04)',
        }}>
          <div style={{ ...S.sectionTitle, color: '#B45309' }}>
            ⚠️ Attenzione: Agenti Disattivi
          </div>
          <p style={{ fontSize: '0.88rem', color: '#92400E', lineHeight: 1.6, margin: 0 }}>
            Il conteggio degli agenti disattivi risulta <strong>0</strong>, ma potrebbero esserci{' '}
            <strong>666 agenti disattivi</strong> non rilevati dalla gerarchia corrente.
            Questo accade perché la tabella <code>tab_agenti_sottoposti_diretti</code>{' '}
            non include gli agenti del canale Infinity che risultano disattivi.{' '}
            Il backend è stato corretto per includerli.
          </p>
        </div>
      )}

    </div>
  );
};